/**
 * Relações de responsabilidade, por recurso (T51, CAT-04,
 * `ANALISE-CONTROLE-ACESSO.md` §4.3).
 *
 * Hoje cada relação é uma condição escrita à mão dentro de um `can(...)` de
 * `src/shared/autorizacao/*.ts` — `"siteManager.mail": { $eq: user.email }` e mais
 * doze iguais. Aqui elas viram dado: o catálogo da Fase 7 declara
 * `exigeRelacao: ["site_manager", "suplente"]` e o compilador
 * (`../compilador.ts`) resolve o caminho contra este mapa.
 *
 * **Só nomes e caminhos — nenhuma concessão.** Quem concede o quê é o
 * `PAPEIS_<Dominio>` (T52 e T53).
 *
 * **`campo` é caminho no REGISTRO, não no usuário.** Profundidade é livre e array
 * intermediário funciona — medido em T51 contra o `createMongoAbility` deste
 * repositório: `obra.matriz.responsaveis.mail` casa quando um dos elementos do
 * array bate, e nega quando nenhum bate. O modo de falha que importa é outro: se o
 * caminho atravessa uma entidade que veio só como id (`RDO { obra: "OBRA-317" }`),
 * a regra **nega em silêncio**, sem erro nenhum. É por isso que o spec deste
 * arquivo confere cada caminho contra a política imperativa que ainda existe: um
 * typo aqui não quebra, só tira acesso de quem tem.
 *
 * **`rotulo` vai literalmente para a tela** (`T107`/`T108`), na frase "apenas
 * quando a pessoa é o <rótulo>". Português de negócio, minúsculo, sem jargão de
 * campo.
 */
import { subject } from "@casl/ability";
import type { Recurso } from "./vocabulario";

/** Uma relação de responsabilidade: onde ela está no registro e como se chama para gente. */
export interface Relacao {
  /** Caminho do campo dentro do registro checado. Ponto separa nível; array intermediário resolve. */
  campo: string;
  /** Nome da relação em português de negócio. Vai para a tela. */
  rotulo: string;
}

/**
 * As 13 relações hoje em uso, em 5 recursos.
 *
 * Levantadas por varredura de `"<campo>": { $eq: user.email }` sobre
 * `src/shared/autorizacao/*.ts` em 2026-09-05 — não é aspiracional, é o que a
 * política imperativa já faz. O `ADR-0003` diz "9 relações em 5 recursos"; a
 * contagem real é **13**, e os recursos são os mesmos cinco.
 *
 * A grafia dos campos é a do backend e é inconsistente de propósito
 * (`siteManager` em RDO, `Site_manager` em Materiais). Normalizar é mudança de
 * contrato de API, não desta tarefa.
 */
export const RELACOES = {
  RDO: {
    site_manager: { campo: "siteManager.mail", rotulo: "site manager do diário" },
    suplente: { campo: "siteManagerSuplente.mail", rotulo: "site manager suplente do diário" },
  },
  /**
   * As três relações da Obra (T62, ESC-07) — site manager, suplente e gestor.
   * `ObraModel.ts` já declara os três campos (`siteMananger`, `siteManagerSuplente`,
   * `gestor`, todos `Pessoa`); aqui eles viram dado, como o cabeçalho do arquivo
   * descreve para os outros cinco recursos.
   *
   * **Por que existem, medido contra T89/AD-046 (2026-09-08):** `papeis/obra.ts`
   * não tem concessão condicional nenhuma hoje — `CONCESSOES_BASE_OBRA` e os três
   * papéis de `PAPEIS_OBRA` são incondicionais, e isso está certo como está (a
   * T89 mediu e não há regressão a corrigir). Estas relações não condicionam
   * nada em `Obra`. Elas existem para o **outro** lado do problema que o ESC-07
   * descreve: `RELACOES.RDO.suplente` (`siteManagerSuplente.mail`) não resolve em
   * `src/shared/rdo/RDO.ts` (exceção BE-12, ver `invariantes-escopo.spec.ts`) porque
   * o campo não é do RDO — é da Obra. `RELACOES.Obra` é a fonte real desses dois
   * campos, e `prototipoDeRdoParaCreate` (abaixo) os empresta para o `create` de
   * RDO, cujo registro ainda não existe.
   *
   * **`gestor` é declarado e ainda não tem consumidor** — medido: nenhuma
   * concessão do catálogo referencia `RELACOES.Obra.gestor` hoje. Fica junto dos
   * outros dois porque o `What` desta tarefa pede os três nomeados, e porque
   * `ObraModel.ts:108` já guarda o dado; `R4` (anti-inflação) veda kernel "para o
   * futuro", não uma relação a mais numa tabela que já existe com consumidor.
   */
  Obra: {
    site_manager: { campo: "siteMananger.mail", rotulo: "site manager da obra" },
    suplente: { campo: "siteManagerSuplente.mail", rotulo: "site manager suplente da obra" },
    gestor: { campo: "gestor.mail", rotulo: "gestor da obra" },
  },
  Materiais: {
    quantificou: { campo: "Quantificou.mail", rotulo: "quem quantificou a lista" },
    verificador: { campo: "Verificador.mail", rotulo: "verificador da lista" },
    coordenador: { campo: "Coordenador.mail", rotulo: "coordenador da lista" },
    site_manager: { campo: "Site_manager.mail", rotulo: "site manager da lista" },
  },
  Contratos: {
    responsavel: { campo: "responsavel.mail", rotulo: "responsável pelo contrato" },
    gestor: { campo: "gestor.mail", rotulo: "gestor do contrato" },
    validador: { campo: "validador.mail", rotulo: "validador do contrato" },
  },
  ProcessoJuridico: {
    responsavel_dp: { campo: "responsavelDp.mail", rotulo: "responsável de DP pelo processo" },
    responsavel_seg: {
      campo: "responsavelSeg.mail",
      rotulo: "responsável de segurança pelo processo",
    },
  },
  TicketInteracao: {
    responsavel: { campo: "responsavel.mail", rotulo: "responsável pela interação" },
    responsavel_registro: {
      campo: "responsavelRegistro.mail",
      rotulo: "responsável pelo registro do ticket",
    },
  },
} as const satisfies Partial<Record<Recurso, Record<string, Relacao>>>;

/** Recursos que têm relação declarada. */
export type RecursoComRelacao = keyof typeof RELACOES;

/** Nomes de relação válidos para um recurso — `NomeRelacao<"RDO">` é `"site_manager" | "suplente"`. */
export type NomeRelacao<R extends RecursoComRelacao> = keyof (typeof RELACOES)[R] & string;

/**
 * O campo que guarda o estado do registro, por recurso.
 *
 * `Concessao.estados` não pode assumir um nome único: medido nos arquivos
 * imperativos, Contratos usa `status` e Materiais usa `Status` — mesma palavra,
 * caixa diferente, e o matcher do CASL diferencia. Um compilador que fixasse
 * `Status` negaria toda concessão de Contratos em silêncio.
 *
 * Recurso ausente aqui e com `estados` na concessão é erro do catálogo, e o
 * compilador levanta em vez de negar.
 */
export const CAMPO_ESTADO = {
  Contratos: "status",
  Materiais: "Status",
  PROJETO: "Situacao",
} as const satisfies Partial<Record<Recurso, string>>;

/** Recursos que têm campo de estado declarado. */
export type RecursoComEstado = keyof typeof CAMPO_ESTADO;

/**
 * Lê um caminho ponteado (`"siteMananger.mail"`) num registro qualquer.
 * Qualquer segmento ausente propaga `undefined` — o mesmo modo de falha
 * silenciosa que o cabeçalho deste arquivo descreve para `campoDaRelacao`.
 */
function valorNoCaminho(registro: unknown, caminho: string): unknown {
  return caminho.split(".").reduce<unknown>((atual, parte) => {
    if (atual === null || typeof atual !== "object") return undefined;
    return (atual as Record<string, unknown>)[parte];
  }, registro);
}

/**
 * Protótipo de RDO construído a partir da Obra, para checar `create` ANTES de
 * o registro de RDO existir (T62, ESC-07).
 *
 * `PAPEIS_RDO."rdo.escrita"` exige a relação `site_manager`/`suplente` sobre o
 * recurso `RDO` (`RELACOES.RDO`), resolvida contra `siteManager.mail` /
 * `siteManagerSuplente.mail` do registro checado. Em `create` esse registro
 * ainda não existe — só a Obra existe, e é dela que esses dois campos vêm de
 * verdade (`RELACOES.Obra`, acima). Este protótipo lê os dois campos da Obra e
 * os devolve com o NOME que `RELACOES.RDO` espera, para que
 * `pode("create", subject("RDO", prototipo))` avalie a mesma condição sem
 * precisar carregar (ou inventar) um registro de RDO.
 *
 * Não concede nada por conta própria — é só o reshape. Quem decide continua
 * sendo o catálogo (`PAPEIS_RDO`) e o compilador (`compilarConcessao`).
 *
 * **Sem call site de produção hoje, e isso é medido, não esquecido.** A T89
 * varreu `views/Obra/**` inteiro e não achou nenhum ponto que precise deste
 * protótipo — o ESC-07 do `spec.md` ficou `pending` justamente porque "o caso
 * de `create` que usa o protótipo de obra não existe no código". Esta função
 * é a peça que faltava para quando um domínio migrado (RDO) precisar checar
 * `create` contra a Obra; o `Done when` desta tarefa pede a peça, não o
 * consumidor.
 */
export function prototipoDeRdoParaCreate(obra: unknown): {
  siteManager: { mail: unknown };
  siteManagerSuplente: { mail: unknown };
} {
  return {
    siteManager: { mail: valorNoCaminho(obra, RELACOES.Obra.site_manager.campo) },
    siteManagerSuplente: { mail: valorNoCaminho(obra, RELACOES.Obra.suplente.campo) },
  };
}

/**
 * Sujeito de RDO decidido pela OBRA ATUAL, não pelo retrato gravado no diário
 * (RDO-02, conferência de acessos).
 *
 * O diário guarda o site manager de quando foi criado — e `Diario.siteManager`
 * nem tem o formato `{ mail }` que a relação lê. Quem muda de site manager na
 * obra perdia o direito de editar diários antigos. Aqui o diário sai com
 * `siteManager`/`siteManagerSuplente` vindos da obra atual (`RELACOES.Obra`), o
 * mesmo reshape de `prototipoDeRdoParaCreate`; o resto do registro fica como está.
 *
 * Sem obra carregada (`null`, `undefined` ou `{}`, o valor inicial das telas) a
 * função devolve o diário intacto — o comportamento de antes.
 */
export function sujeitoDeRdoComObra<T extends object>(rdo: T, obra: unknown): T {
  const semObra =
    obra === null ||
    obra === undefined ||
    (typeof obra === "object" && Object.keys(obra).length === 0);
  if (semObra) return rdo;

  const clone = Object.assign(
    Object.create(Object.getPrototypeOf(rdo)),
    rdo,
    prototipoDeRdoParaCreate(obra),
  );
  return subject("RDO", clone) as T;
}
